import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { BackArrow, IconButton } from 'qampo-ui-library';
import { Table, useTableSort } from '../UI';
import { Button } from '../UI/base/Button';
import { InterventionFormModal } from '../Configuration/InterventionFormModal';
import { DeleteInterventionModal } from '../Configuration/DeleteInterventionModal';
import { useInterventionTemplates } from '../../hooks/useInterventionTemplates';
import { useStaffCompetences } from '../../hooks/useStaffCompetences';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { showError } from '../../store/errorDialog/errorDialogSlice';
import { showToast } from '../../store/toast/toastSlice';
import { selectHasFetchedStaffCompetences } from '../../store/staffCompetences/staffCompetencesSlice';
import { getApiErrorMessage, getHttpStatus } from '../../utils/errorMessageUtils';
import {
  buildDuplicateName,
  findArchivedCompetenceIds,
  toCreateRequest,
  toUpdateRequest
} from '../../services/interventionTemplateMappers';
import ROUTES from '../../routes/routeConstants';
import type { InterventionTemplateResponse } from '../../api/generated';
import { getInterventionColumns } from './interventionColumns';
import {
  ARCHIVED_COMPETENCE_CONFLICT_STATUS,
  DEFAULT_INTERVENTIONS_SORT,
  INTERVENTIONS_TABLE_MIN_WIDTH
} from './interventionsConstants';
import styles from './InterventionsList.module.css';

export const InterventionsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { templates, isLoading, loadError, createTemplate, updateTemplate, deleteTemplate } =
    useInterventionTemplates();
  const { competences } = useStaffCompetences();

  const [interventionModalOpen, setInterventionModalOpen] = useState(false);
  const [editingIntervention, setEditingIntervention] =
    useState<InterventionTemplateResponse | null>(null);
  const [deleteConfirmIntervention, setDeleteConfirmIntervention] =
    useState<InterventionTemplateResponse | null>(null);

  const { sort, handleSortChange } = useTableSort(DEFAULT_INTERVENTIONS_SORT);

  // True only after a successful competence load; a failed one leaves it false so the
  // archived pre-check stays disabled rather than misreading an empty list.
  const hasFetchedCompetences = useAppSelector(selectHasFetchedStaffCompetences);

  // Guards against re-entrant duplicate calls. State rather than a ref is enough because
  // ContextMenu closes on item activation, so a second Dupliker click needs the menu
  // reopened, and this flag has committed by then.
  const [isDuplicating, setIsDuplicating] = useState(false);

  const professionNameById = useMemo(
    () => new Map(competences.map((c) => [c.id, c.name])),
    [competences]
  );

  // The competence list endpoint defaults to excluding archived entries and this project
  // never asks for them, so once loaded this set is exactly the non-archived ones and
  // anything missing from it has been archived. Before it loads it is simply empty, which
  // is why every read of it is gated on `hasFetchedCompetences`.
  const liveCompetenceIds = useMemo(
    () => new Set(competences.map((c) => c.id)),
    [competences]
  );

  const handleBack = (): void => {
    navigate(`/${ROUTES.configuration}`);
  };

  const handleOpenInterventionModal = (
    intervention: InterventionTemplateResponse | null = null
  ): void => {
    setEditingIntervention(intervention);
    setInterventionModalOpen(true);
  };

  const handleSaveIntervention = async (
    intervention: InterventionTemplateResponse
  ): Promise<void> => {
    const isUpdate = editingIntervention !== null;
    try {
      if (isUpdate) {
        await updateTemplate(intervention.id, toUpdateRequest(intervention));
      } else {
        await createTemplate(toCreateRequest(intervention));
      }
      setInterventionModalOpen(false);
      setEditingIntervention(null);
    } catch (e) {
      console.error('Failed to save intervention:', e);
      const fallback = isUpdate ? t('config-iv-update-error') : t('config-iv-create-error');
      dispatch(showError({ message: getApiErrorMessage(e, fallback) }));
    }
  };

  // Duplicating creates the copy straight away rather than opening a prefilled form,
  // matching how treatment plan templates duplicate (REH-323). The copy is then edited
  // like any other row.
  const handleDuplicateIntervention = useCallback(
    async (intervention: InterventionTemplateResponse): Promise<void> => {
      // The backend validates archived professions on create as well as update, so a
      // template that predates an archiving keeps working while a copy of it would be
      // rejected. Say so here instead of posting a request that is known to fail: the
      // fix is on the original, which is the only place the admin can make it.
      //
      // Only meaningful once the competence list has actually arrived. `hasFetched` is
      // set on a successful load alone, so before that -- and after a failed load -- the
      // set is empty and every referenced competence would read as archived, blocking a
      // perfectly healthy intervention with a message telling the admin to fix it. In
      // that state, skip the pre-check and let the backend's 409 come back through the
      // catch below, which says the same thing when it really is the cause.
      if (
        hasFetchedCompetences &&
        findArchivedCompetenceIds(intervention, liveCompetenceIds).length > 0
      ) {
        dispatch(showError({ message: t('config-iv-duplicate-archived-error') }));
        return;
      }

      const name = buildDuplicateName(
        intervention.name,
        templates.map((iv) => iv.name),
        t
      );
      // `name` is computed from the `templates` in this closure, which only grows once a
      // create resolves. Without this, two quick clicks both pick the same free suffix and
      // the backend accepts both, producing exactly the identical names this feature
      // exists to prevent.
      if (isDuplicating) return;
      setIsDuplicating(true);
      try {
        await createTemplate(toCreateRequest({ ...intervention, name }));
        // With no modal and no navigation, the toast is the only confirmation the copy
        // exists; under a non-default sort the new row can also land off screen.
        dispatch(showToast({ message: t('config-iv-duplicated', { name }) }));
      } catch (e) {
        console.error('Failed to duplicate intervention:', e);
        // The backend answers a referenced archived competence with 409. That is either
        // the pre-check being skipped because the competence list never loaded, or the
        // competence having been archived by someone else since this page loaded; both
        // deserve the specific message rather than the generic create failure.
        const fallback =
          getHttpStatus(e) === ARCHIVED_COMPETENCE_CONFLICT_STATUS
            ? t('config-iv-duplicate-archived-error')
            : t('config-iv-create-error');
        dispatch(showError({ message: getApiErrorMessage(e, fallback) }));
      } finally {
        setIsDuplicating(false);
      }
    },
    [templates, liveCompetenceIds, hasFetchedCompetences, isDuplicating, createTemplate, dispatch, t]
  );

  const handleConfirmDelete = async (): Promise<void> => {
    if (!deleteConfirmIntervention) return;
    const idToDelete = deleteConfirmIntervention.id;
    setDeleteConfirmIntervention(null);
    try {
      await deleteTemplate(idToDelete);
    } catch (e) {
      console.error('Failed to delete intervention:', e);
      dispatch(showError({ message: getApiErrorMessage(e, t('config-iv-delete-error')) }));
    }
  };

  const columns = useMemo(
    () =>
      getInterventionColumns(t, {
        onEdit: (iv) => handleOpenInterventionModal(iv),
        onDuplicate: (iv) => void handleDuplicateIntervention(iv),
        onDelete: (iv) => setDeleteConfirmIntervention(iv),
        professionNameById
      }),
    // handleDuplicateIntervention closes over `templates`, which the name-uniqueness
    // check has to read fresh, so the columns rebuild when the list changes.
    [t, professionNameById, handleDuplicateIntervention]
  );

  const isInitialLoading = isLoading && templates.length === 0;
  const isEmptyState = !isLoading && !loadError && templates.length === 0;

  return (
    <>
      <div className={styles.topBar}>
        <nav className={styles.breadcrumbRow} aria-label={t('breadcrumb')}>
          <IconButton icon={<BackArrow />} onClick={handleBack} />
          <div className={styles.breadcrumbStack}>
            <button type="button" className={styles.breadcrumbLink} onClick={handleBack}>
              {t('configuration')}
            </button>
            <span className={styles.breadcrumbCurrent}>{t('config-tab-interventions')}</span>
          </div>
        </nav>
        <Button type="primary" onClick={() => handleOpenInterventionModal(null)}>
          {t('config-add-intervention')}
        </Button>
      </div>

      {isInitialLoading && <p className={styles.emptyState}>{t('config-iv-loading')}</p>}

      {loadError && <p className={styles.emptyState}>{t('config-iv-load-error')}</p>}

      {isEmptyState && <p className={styles.emptyState}>{t('config-iv-empty')}</p>}

      {templates.length > 0 && (
        <Table
          columns={columns}
          data={templates}
          sort={sort}
          onSortChange={handleSortChange}
          getRowKey={(iv) => iv.id}
          minWidth={INTERVENTIONS_TABLE_MIN_WIDTH}
        />
      )}

      {interventionModalOpen && (
        <InterventionFormModal
          intervention={editingIntervention}
          onSave={handleSaveIntervention}
          onClose={() => {
            setInterventionModalOpen(false);
            setEditingIntervention(null);
          }}
        />
      )}

      {deleteConfirmIntervention && (
        <DeleteInterventionModal
          interventionName={deleteConfirmIntervention.name}
          onConfirm={handleConfirmDelete}
          onClose={() => setDeleteConfirmIntervention(null)}
        />
      )}
    </>
  );
};
